import React, { useEffect, useState } from 'react';
import './OurReviews.css';
import { Helmet } from 'react-helmet-async';

// Helper to chunk reviews into rows of 4 (adjust if you want a different layout)
function chunkArray(arr, chunkSize) {
  const chunks = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    chunks.push(arr.slice(i, i + chunkSize));
  }
  return chunks;
}

const OurReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/public/reviews`);
        if (!res.ok) throw new Error('Failed to fetch reviews');
        const { reviews } = await res.json();
        setReviews(reviews || []);
      } catch {
        setError('Failed to load reviews.');
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  // Skeleton placeholders (adjust number for your typical review count or max per page)
  const PLACEHOLDER_COUNT = 16;
  const placeholders = Array.from({ length: PLACEHOLDER_COUNT }, (_, i) => i);


    let aggregateRating;
  let reviewStructured = [];

  if (!loading && !error && reviews.length > 0) {
    const sum = reviews.reduce((a, r) => a + (parseInt(r.rating) || 0), 0);
    aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": (sum / reviews.length).toFixed(2),
      "reviewCount": reviews.length.toString()
    };
    reviewStructured = reviews.slice(0, 20).map((r) => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": "Customer"
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": String(r.rating)
      },
      "reviewBody": r.review_text
    }));
  }


  return (
    <>
            <Helmet>
                <title>Reviews | Marion County, FL | Watkins Seamless Gutters</title>
                <meta 
                    name="description" 
                    content="Watkins Seamless Gutters is known for the highest quality of gutter service in Lake, Sumter and Marion Counties of Florida. Click here to read our reviews." 
                />

                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content="Reviews | Marion County, FL | Watkins Seamless Gutters" />
                <meta name="twitter:description" content="Watkins Seamless Gutters is known for the highest quality of gutter service in Lake, Sumter and Marion Counties of Florida. Click here to read our reviews." />

                <meta property="og:description" content="Watkins Seamless Gutters is known for the highest quality of gutter service in Lake, Sumter and Marion Counties of Florida. Click here to read our reviews." />
                <meta property="og:title" content="Reviews | Marion County, FL | Watkins Seamless Gutters" />

                <link rel="canonical" href="https://www.watkinsgutters.com/testimonials" />
                {!loading && !error && reviews.length > 0 && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "LocalBusiness",
                        "name": "Watkins Seamless Gutters",
                        "url": "https://www.watkinsgutters.com/testimonials",
                        "telephone": "352-812-0831",
                        "email": "GuttersByWatkins@gmail.com",
                        "address": {
                            "@type": "PostalAddress",
                            "addressLocality": "Marion County",
                            "addressRegion": "FL",
                            "addressCountry": "US"
                        },
                        "areaServed": {
                            "@type": "Place",
                            "name": [
                                "Sumter County, FL", 
                                "Marion County, FL", 
                                "Lake County, FL", 
                                "Ocala, FL", 
                                "Ocklawaha, FL",  
                                "Summerfield, FL",
                                "Leesburg, FL", 
                                "The Villages, FL"
                            ]
                        },
                        "sameAs": [
                            "https://www.facebook.com/GuttersByWatkins"
                        ],
                        "aggregateRating": aggregateRating,
                        "review": reviewStructured
                        })}
                    </script>
                )}
            </Helmet>
      <div className='page-wrapper'>
        <div className='page-content-container'>
          <div className='content-wrapper'>
            <div className='page-content'>
              <div className='page-content-wrapper'>
                <div className='page-title-wrapper'>
                  <div className='page-title'>
                    <h1>Top Rated Reviews of Our Work!</h1>
                  </div>
                </div>
                <div className='testimonial-row-wrapper'>
                  {/* SKELETON LOADING */}
                  {loading ? (
                    chunkArray(placeholders, 4).map((row, rowIdx) => (
                      <div className='testimonial-row' key={rowIdx}>
                        {row.map((_, colIdx) => (
                          <div className='review-card' key={colIdx} style={{ minHeight: 170 }}>
                            <div className='review-content'>
                              <div className="review-skeleton-stars" aria-hidden="true" />
                            </div>
                            <div className='review-content'>
                              <div className="review-skeleton-text" aria-hidden="true" />
                              <div className="review-skeleton-author" aria-hidden="true" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ))
                  ) : error ? (
                    <div className="gallery-error" role="alert">{error}</div>
                  ) : (
                    reviews.length === 0 ? (
                      <div>No reviews to show yet.</div>
                    ) : (
                      chunkArray(reviews, 4).map((row, rowIdx) => (
                        <div className='testimonial-row' key={rowIdx}>
                          {row.map((review) => (
                            <div className='review-card' key={review.id}>
                              <div className='review-content'>
                                <p aria-label={`Rating: ${review.rating} out of 5`}>
                                  {'⭐'.repeat(review.rating)}
                                  <span style={{ display: 'none' }}>{review.rating} out of 5 stars</span>
                                </p>
                              </div>
                              <div className='review-content'>
                                <p>
                                  {review.review_text}
                                  <br /><br />
                                  <strong>{review.reviewer_name}</strong>
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Add CSS for skeletons below */}
    </>
  );
};

export default OurReviews;
